import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { type Faq } from "@/data/serviceFaqs";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import img from "@/assets/shading.jpg";


const shadingFaqs: Faq[] = [
        {
          q: "How much do motorized shades cost in Denver?",
          a: "Installed motorized shades in Denver typically start around $450 per window for battery-powered roller shades with a quality light-filtering fabric, and $700–$1,800+ per window for hardwired Lutron Sivoia QS, drapery, or dual roller (sheer + blackout) systems. Whole-home projects most often land between $8,000 and $40,000, with luxury Cherry Creek, Castle Pines, and Polo Club estates ranging higher. Every Apex AV Denver quote is fixed-bid before install.",
        },
        {
          q: "Do motorized shades work with smart home systems?",
          a: "Yes. Every motorized shading system we install in Denver integrates with all major whole-home automation platforms, voice assistants, Alexa, Google Home, and Apple Home. Shades trigger from the same keypads and apps that already run your lights, theater, and climate.",
        },
        {
          q: "Battery-powered or hardwired motorized shades — which is right for my Denver home?",
          a: "For new construction and remodels we spec hardwired shades whenever possible — silent precision motors, no batteries to change, and reliable operation for 20+ years. For retrofit projects in existing Denver homes where opening walls isn't practical, we install rechargeable battery-powered shades from leading manufacturers that last 3–5 years per charge.",
        },
        {
          q: "Can motorized shades really cut cooling costs on Denver's south and west windows?",
          a: "Yes — this is one of the biggest wins in Denver. Automated shades on southern and western exposures track the sun and lower automatically during peak solar gain, typically cutting HVAC load 10–25% in summer while still letting light in during shoulder hours. We wire this into your thermostat scenes so the house cools itself before you get home.",
        },
        {
          q: "How long does a Denver motorized shades install take?",
          a: "Measurement and design take about one week. Fabric and motor lead times are typically 3–6 weeks depending on brand. On-install day, our Denver team can hang and program a whole-home shading system in 1–2 days, then return for final calibration once you've lived with the automation for a week.",
        },
        {
          q: "Which neighborhoods do you install motorized shades in?",
          a: "We install motorized shades across the Denver Metro and Front Range — Cherry Creek, Hilltop, Polo Club, Greenwood Village, Cherry Hills Village, Castle Pines, Castle Rock, Highlands Ranch, Lone Tree, Parker, Littleton, Centennial, Bow Mar, Evergreen, and Boulder.",
        },
];

const bullets = [
  "Premium motorized roller, roman, drapery, and cellular shades",
  "Astronomical timeclock — shades follow the Denver sun automatically",
  "Glare and heat-gain control for south and west Front Range exposures",
  "Wired and battery-powered options for retrofits",
  "Hundreds of fabrics, sheers, and blackouts in stock",
  "Integrated with lighting, climate, and automation scenes",
  "Certified installers for all major motorized shading brands",
  "Whole-home Denver motorized shading design & install",
];

export const Route = createFileRoute("/services/shading")({
  head: () => ({
    meta: [
      { title: "Motorized Shades & Window Treatments | Apex AV Denver" },
      { name: "description", content: "Whisper-quiet motorized shades for Denver homes. Roller, roman, drapery, and cellular options that move automatically with the sun." },
      { name: "keywords", content: "motorized shades Denver, automated shades Denver, smart window treatments Denver, roller shades Denver, roman shades Denver, blackout shades Denver, automated window coverings Denver, sun-tracking shades Denver, battery powered shades Denver, luxury window shades Denver" },
      { property: "og:title", content: "Motorized Shading — Apex AV Denver" },
      { property: "og:description", content: "Motorized shades that move with the sun across Denver homes." },
      { property: "og:image", content: abs(img) },
      { name: "twitter:image", content: abs(img) },
      { property: "og:url", content: "https://apexav.com/services/shading" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Motorized Shading — Apex AV Denver" },
      { name: "twitter:description", content: "Motorized shades that move with the sun across Denver homes." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/shading" }, { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "shading",
            name: "Motorized Shading",
            description: "Whisper-quiet motorized shades that follow the Front Range sun.",
            pageTitle: "Motorized Shades & Window Treatments | Apex AV Denver",
            pageDescription: "Whisper-quiet motorized shades for Denver homes. Roller, roman, drapery, and cellular options that move automatically with the sun.",
            image: img,
            faqs: shadingFaqs,
            serviceType: "Motorized Shading",
            breadcrumbName: "Motorized Shading",
            minPrice: 450,
            priceDescription: "Most Denver whole-home motorized shading projects run $8,000–$40,000+ depending on fabric, motor, and window count.",
            offerCatalog: bullets,
            extraNodes: [
              {
                "@type": "VideoObject",
                name: "Motorized Shades & Window Treatments — Apex AV Denver",
                description: "Whisper-quiet motorized shades for Denver homes. Roller, roman, drapery, and cellular options that move automatically with the sun.",
                thumbnailUrl: ["https://apexav.com/videos/shading.jpg"],
                uploadDate: "2026-06-01T00:00:00-06:00",
                contentUrl: "https://apexav.com/videos/shade-opening-video.mp4",
                embedUrl: "https://apexav.com/services/shading",
              },
            ],
          }),
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      topics={["shading", "lighting", "smart-home"]}
      eyebrow="Motorized shades Denver"
      title="Motorized shades that follow the Denver sun."
      intro={<>Denver's trusted installer for <strong>motorized shades</strong> and <strong>automated window treatments</strong>. Whisper-quiet roller, roman, drapery, and blackout shades that track the Front Range sun, cut glare on south- and west-facing glass, protect hardwood and art from UV, and frame the Rocky Mountain view.</>}
      image={img}
      imageAlt="Motorized roller shades and smart window treatments installed in a Greenwood Village Denver home"
      video="/videos/shade-opening-video.mp4"
      startingPrice="$450 per shade installed"
      priceNote="Most Denver whole-home motorized shading projects run $8,000–$40,000+ depending on fabric, motor, and window count."
      bullets={bullets}
      features={[
        { title: "Sun-tracking", desc: "Shades adjust automatically as the Front Range sun moves across the sky." },
        { title: "Protect interiors", desc: "Block UV before it fades hardwood, art, and upholstery." },
        { title: "Whisper quiet", desc: "Near-silent precision motors — even in the bedroom." },
      ]}
      brandsTitle="Motorized shading brands we install in Denver"

      faqs={shadingFaqs}
    />
  ),
});
